/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { ColorName, Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { CTAType, SectionName, useAvroTracking } from '../../../observability';
import { ArrowLink } from '../../ArrowLink/ArrowLink';

export type SimpleFeaturesSectionProps = {
  backgroundColor: ColorName;
  columns: {
    heading: string;
    description: string;
    linkText: string;
    href: string;
  }[];
  disableSpacing?: boolean;
  heading?: string;
};

const Styles = labelStyles('SimpleFeaturesSection', {
  root: (backgroundColor: ColorName, disableSpacing?: boolean) =>
    css({
      backgroundColor: backgroundColor ? Colors[backgroundColor] : 'inherit',
      ...(!disableSpacing && {
        padding: '96px 20px',
        [MEDIA_MIN_WIDTH.medium]: {
          paddingTop: '96px',
          paddingBottom: '151px',
        },
        [MEDIA_MIN_WIDTH.large]: {
          padding: '160px 20px',
        },
      }),
    }),
  content: css({
    margin: '0 auto',
    maxWidth: '512px',
    [MEDIA_MIN_WIDTH.medium]: {
      maxWidth: '1170px',
    },
  }),
  heading: css({
    fontFamily: Typography.heading.fontFamily,
    fontSize: '36px',
    fontWeight: 600,
    lineHeight: '39.6px',
    letterSpacing: '1px',
    textAlign: 'left',
    maxWidth: '320px',
    color: Colors.earth,
    paddingBottom: '32px',
    [MEDIA_MIN_WIDTH.medium]: {
      maxWidth: '512px',
      margin: '0 auto',
      fontSize: '40px',
      letterSpacing: '1.5px',
      lineHeight: '40px',
      textAlign: 'center',
      paddingBottom: '64px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '864px',
      fontSize: '70px',
      lineHeight: '67.2px',
      paddingBottom: '96px',
    },
  }),
  columnsWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '48px',
    [MEDIA_MIN_WIDTH.medium]: {
      flexDirection: 'row',
      gap: '29px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      gap: '68px',
    },
  }),
  column: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  }),
  columnHeading: css({
    fontFamily: Typography.base.fontFamily,
    color: Colors.earth,
    fontSize: '19px',
    lineHeight: '23px',
    marginBottom: '16px',
    [MEDIA_MIN_WIDTH.medium]: {
      marginBottom: '9px',
      lineHeight: '32px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '26px',
      lineHeight: '31.2px',
      letterSpacing: '-0.5px',
      marginBottom: '32px',
    },
  }),
  description: css({
    fontFamily: Typography.base.fontFamily,
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    marginBottom: '16px',
    [MEDIA_MIN_WIDTH.medium]: {
      fontSize: '18px',
      marginBottom: '18px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '18px',
      marginBottom: '24px',
    },
  }),
});

export function SimpleFeaturesSection(props: SimpleFeaturesSectionProps) {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div css={Styles.root(props.backgroundColor, props.disableSpacing)}>
      <div css={Styles.content}>
        {props.heading && <h2 css={Styles.heading}>{props.heading}</h2>}
        <div css={Styles.columnsWrapper}>
          {(props.columns || []).map((column) => (
            <div css={Styles.column} key={column.heading}>
              <div>
                <h3 css={Styles.columnHeading}>{column.heading}</h3>
                <p css={Styles.description}>{column.description}</p>
              </div>
              <ArrowLink
                href={column.href}
                onClick={() => {
                  emitCTAClickEvent({
                    text: column.linkText,
                    href: column.href,
                    type: CTAType.cta_link,
                    section: SectionName.SimpleFeaturesSection,
                  });
                }}
              >
                {column.linkText}
              </ArrowLink>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
